/**
 * AUTH-1F — admin-generated password reset link handoff.
 *
 * Security: the reset URL is shown only inside this modal. Never toast it,
 * never console-log it, never auto-copy — copy requires an explicit click.
 */
import { useEffect, useState } from "react";
import { Check, Copy, Loader2 } from "lucide-react";

import { Modal } from "@/components/ui/overlay/Modal";
import { Button } from "@/components/ui/button/Button";
import { administrationApi } from "@/features/administration/api/administration.api";

const HONEST_COPY =
  "This link expires in 1 hour and can be used once. Share it directly with the user — email delivery is not enabled yet.";

const RESET_LINK_ERROR_CODE = {
  SELF: "RESET_LINK_SELF_FORBIDDEN",
  HIERARCHY: "RESET_LINK_HIERARCHY_FORBIDDEN",
  NOT_MEMBER: "RESET_LINK_TARGET_NOT_MEMBER",
  SHARED_ACCOUNT: "RESET_LINK_TARGET_SHARED_ACCOUNT",
} as const;

/** Fallback when the response names a code but carries no usable message. */
const RESET_LINK_FALLBACK = {
  SELF: "You cannot generate a reset link for yourself. Change your password from your profile instead.",
  HIERARCHY: "A reset link cannot be generated for this person.",
  NOT_MEMBER:
    "This user is not an active member of your organization, so a reset link cannot be generated.",
  SHARED_ACCOUNT:
    "A reset link can't be generated for this user because their account is shared with another organization. They can reset their own password from the sign-in page.",
  ORG: "You can only generate reset links for people in your organization.",
  NOT_FOUND: "That user was not found. They may have been removed from the organization.",
  GENERIC: "Could not generate a reset link. Try again.",
} as const;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userLabel?: string | null;
};

function readResetLinkError(err: unknown): {
  status?: number;
  code?: string;
  message?: string;
} {
  const ax = err as {
    response?: { status?: number; data?: { code?: unknown; message?: unknown } };
  };
  const data = ax?.response?.data;
  return {
    status: ax?.response?.status,
    code: typeof data?.code === "string" ? data.code : undefined,
    message: typeof data?.message === "string" ? data.message : undefined,
  };
}

function usableServerMessage(message: string | undefined): string | undefined {
  const trimmed = message?.trim() ?? "";
  if (!trimmed || trimmed === "Forbidden" || trimmed.length > 200) return undefined;
  return trimmed;
}

function mapResetLinkError(err: unknown): string {
  const { status, code, message } = readResetLinkError(err);
  const server = usableServerMessage(message);
  if (code === RESET_LINK_ERROR_CODE.SELF) return server ?? RESET_LINK_FALLBACK.SELF;
  if (code === RESET_LINK_ERROR_CODE.HIERARCHY) return server ?? RESET_LINK_FALLBACK.HIERARCHY;
  if (code === RESET_LINK_ERROR_CODE.NOT_MEMBER) return server ?? RESET_LINK_FALLBACK.NOT_MEMBER;
  if (code === RESET_LINK_ERROR_CODE.SHARED_ACCOUNT) return server ?? RESET_LINK_FALLBACK.SHARED_ACCOUNT;
  if (status === 403) return RESET_LINK_FALLBACK.ORG;
  if (status === 404) return RESET_LINK_FALLBACK.NOT_FOUND;
  return RESET_LINK_FALLBACK.GENERIC;
}

export function SendResetLinkDialog({ isOpen, onClose, userId, userLabel }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen || !userId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setResetLink(null);
    setCopied(false);

    void (async () => {
      try {
        const result = await administrationApi.generateUserResetLink(userId);
        if (cancelled) return;
        setResetLink(result.resetLink);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(mapResetLinkError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  function handleClose() {
    setLoading(false);
    setError(null);
    setResetLink(null);
    setCopied(false);
    onClose();
  }

  async function handleCopy() {
    if (!resetLink) return;
    try {
      await navigator.clipboard.writeText(resetLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const title = userLabel ? `Reset link for ${userLabel}` : "Password reset link";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="md">
      <div className="space-y-4">
        {loading ? (
          <div
            className="flex items-center gap-2 text-sm text-gray-600"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Generating reset link…
          </div>
        ) : null}

        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && resetLink ? (
          <>
            <p className="text-sm text-gray-600">{HONEST_COPY}</p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={resetLink}
                aria-label="Password reset link"
                className="min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-900"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button type="button" variant="outline" onClick={() => void handleCopy()}>
                {copied ? (
                  <>
                    <Check className="mr-1 h-4 w-4" aria-hidden />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="mr-1 h-4 w-4" aria-hidden />
                    Copy
                  </>
                )}
              </Button>
            </div>
            {copied ? (
              <p className="text-xs text-gray-500" aria-live="polite">
                Link copied to clipboard.
              </p>
            ) : null}
          </>
        ) : null}

        <div className="flex justify-end pt-1">
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export const SEND_RESET_LINK_HONEST_COPY = HONEST_COPY;
